import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';

import leadService from '../../services/leadService';
import { getApiError } from '../../utils/apiError';

interface ResendQuestionnaireDialogProps {
  open: boolean;
  onClose: () => void;
  leadId: number;
  leadName: string;
  phone: string | null;
  onSent: (text: string) => void;
}

/**
 * Send the district questionnaire over WhatsApp again.
 *
 * There is nothing to choose, so this asks one question rather than offering a
 * form: a district referral has one questionnaire and it goes out as a WhatsApp
 * template.
 *
 * The phone number is on screen before the button, and that is the point of the
 * dialog rather than its decoration. A referral was stored with a "+44" prefix
 * on an Israeli number, WhatsApp accepted it because it is well formed, and the
 * screen reported a send that reached nobody. Nothing in the code could catch
 * that. A person reading the number can.
 */
const ResendQuestionnaireDialog: React.FC<ResendQuestionnaireDialogProps> = ({
  open,
  onClose,
  leadId,
  leadName,
  phone,
  onSent,
}) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await leadService.resendQuestionnaire(leadId);
      // Say where it went, not that it went. WhatsApp reports a message as
      // accepted, never as read, so "sent to <number>" is the strongest true
      // claim available - and it is the one that exposes a wrong number.
      onSent(`Questionnaire sent to ${result?.phone || phone}`);
      onClose();
    } catch (err) {
      setError(getApiError(err, 'Failed to send the questionnaire'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Send Questionnaire</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Send the questionnaire to {leadName} on WhatsApp?
        </Typography>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: '1.05rem',
            direction: 'ltr',
            textAlign: 'center',
          }}
        >
          {phone || '—'}
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
          Check the number before sending. The message goes to this number as it
          is written here.
        </Typography>
        {error && (
          <Typography variant="body2" sx={{ mt: 2 }} color="error">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={sending || !phone}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResendQuestionnaireDialog;
